import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Modal } from './Modal';
import { R, btnAccent, btnDangerSolid, btnOutline, field } from '../lib/ui';

interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Скрывает «Отмена» — окно превращается в простое сообщение. */
  alert?: boolean;
}

interface PromptOptions {
  title: string;
  body?: string;
  placeholder?: string;
  value?: string;
  confirmLabel?: string;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;
/** Сообщение с одной кнопкой — для «не получилось» и подобного. */
type Alert = (options: { title: string; body?: string }) => Promise<void>;
/** Возвращает введённую строку или null, если отменили. */
type Prompt = (options: PromptOptions) => Promise<string | null>;

const Ctx = createContext<{ confirm: Confirm; prompt: Prompt; alert: Alert } | null>(null);

export function useDialog() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDialog: нет DialogHost в дереве');
  return ctx;
}

/**
 * Свой диалог вместо браузерного confirm: он умеет заголовок, пояснение
 * и красную кнопку для необратимых действий.
 */
export function DialogHost({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const [promptOptions, setPromptOptions] = useState<PromptOptions | null>(null);
  const [draft, setDraft] = useState('');
  const promptResolver = useRef<((value: string | null) => void) | null>(null);

  const confirm = useCallback<Confirm>((next) => {
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const prompt = useCallback<Prompt>((next) => {
    setDraft(next.value ?? '');
    setPromptOptions(next);
    return new Promise<string | null>((resolve) => {
      promptResolver.current = resolve;
    });
  }, []);

  const alert = useCallback<Alert>(
    async (options) => {
      await confirm({ ...options, alert: true, confirmLabel: 'Понятно' });
    },
    [confirm],
  );

  const settle = (value: boolean) => {
    setOptions(null);
    resolver.current?.(value);
    resolver.current = null;
  };

  const settlePrompt = (value: string | null) => {
    setPromptOptions(null);
    promptResolver.current?.(value);
    promptResolver.current = null;
  };

  return (
    <Ctx.Provider value={{ confirm, prompt, alert }}>
      {children}

      {options && (
        <Modal title={options.title} onClose={() => settle(false)} width={420}>
          {options.body && (
            <p style={{ fontSize: 13.5, lineHeight: 1.5, color: R.textSecondary }}>
              {options.body}
            </p>
          )}

          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}
          >
            {!options.alert && (
              <button type="button" onClick={() => settle(false)} style={btnOutline}>
                {options.cancelLabel ?? 'Отмена'}
              </button>
            )}
            <button
              type="button"
              autoFocus
              onClick={() => settle(true)}
              style={options.danger ? btnDangerSolid : btnAccent}
            >
              {options.confirmLabel ?? (options.danger ? 'Удалить' : 'Продолжить')}
            </button>
          </div>
        </Modal>
      )}
      {promptOptions && (
        <Modal title={promptOptions.title} onClose={() => settlePrompt(null)} width={420}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              settlePrompt(draft);
            }}
          >
            {promptOptions.body && (
              <p
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: R.textSecondary,
                  marginBottom: 10,
                }}
              >
                {promptOptions.body}
              </p>
            )}

            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={promptOptions.placeholder}
              style={field}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button type="button" onClick={() => settlePrompt(null)} style={btnOutline}>
                Отмена
              </button>
              <button type="submit" disabled={!draft.trim()} style={btnAccent}>
                {promptOptions.confirmLabel ?? 'Сохранить'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </Ctx.Provider>
  );
}
